import { ApolloQueryResult } from '@apollo/client';
import { isOneOf, Role } from 'auth';
import { AuthState } from 'auth/dist/contexts/auth_context';
import {
  FinancialFilters,
  OutstandingReportRow,
  TransactionReport,
} from 'components/financials/types';
import { ReportType } from 'components/pages/financials';
import { GqlOrganization } from 'core/gql/fragments/organization';
import { Dispatch } from 'react';
import { FinancialsAction } from './reducer';
import {
  getDeposits,
  getOrganizationByOrgId,
  getOutstandingReport,
  getTransactions,
} from './service';
import { ActionType, LoadingType } from './types';

// These are the methods that are EXPOSED to callers of
// useFinancials hook.
export interface FinancialsActions {
  setOrgAdminAccess: (orgId: string) => boolean;
  getOrganizationByOrgId: (orgId: number) => Promise<void>;
  updateDepositsFilter: (key: string, value: any) => void;
  getDeposits: (
    orgId: number,
    dateColumn: string,
    startDate: Date,
    endDate: Date
  ) => Promise<void>;
  updateOutstandingFilter: (key: string, value: any) => void;
  getOutstandingReports: (
    orgId: number,
    startDate: Date,
    endDate: Date
  ) => Promise<void>;
  setOutstandingFilters: (filters: Partial<FinancialFilters>) => void;
  getTransactions: (
    orgId: number,
    dateColumn: string,
    startDate: Date,
    endDate: Date
  ) => Promise<void>;
  updateTransactionsFilter: (key: string, value: any) => void;
  setTransactionsFilters: (filters: Partial<FinancialFilters>) => void;
}

/**
 * These methods are exposed to the components that use the financials context.
 * @param authContext The current state of the auth context
 * @param dispatch The function to call to update the financials state
 * @returns Returns an object containing the methods that can be called to update the financials state.
 */
export const getActions = (
  authContext: AuthState,
  dispatch: Dispatch<FinancialsAction>
): FinancialsActions => {
  async function makeRequest<T>(
    request: (...args: any[]) => Promise<ApolloQueryResult<T>>,
    loadingType?: LoadingType,
    ...args: any[]
  ): Promise<ApolloQueryResult<T>> {
    let response: ApolloQueryResult<T>;
    if (loadingType === undefined) {
      response = await request(...args);
    } else {
      dispatch({
        type: ActionType.SetLoading,
        loading: { loadingType, isLoading: true },
      });
      response = await request(...args);
      dispatch({
        type: ActionType.SetLoading,
        loading: { loadingType, isLoading: false },
      });
    }
    return response;
  }
  return {
    setOrgAdminAccess: (orgId) => {
      const user = authContext?.user;
      const hasOrgAdminAccess =
        user !== null
          ? isOneOf(`${orgId}`, user, Role.Admin, Role.Owner, Role.OrgAdmin)
          : false;
      dispatch({
        type: ActionType.SetOrgAdminAccess,
        payload: { hasOrgAdminAccess },
      });
      return hasOrgAdminAccess;
    },
    async getOrganizationByOrgId(orgId) {
      if (!orgId) {
        return;
      }
      const { data: response } = await makeRequest<{
        showOrganization: GqlOrganization;
      }>(getOrganizationByOrgId, LoadingType.GetOrganization, orgId);
      const { showOrganization: organization } = response;
      dispatch({
        type: ActionType.SetOrganization,
        payload: { organization },
      });
    },
    async getDeposits(orgId, dateColumn, startDate, endDate) {
      if (!orgId) {
        return;
      }
      const { data: response } = await makeRequest<{
        depositFinancialReport: TransactionReport[];
      }>(
        getDeposits,
        LoadingType.GetDeposits,
        orgId,
        dateColumn,
        startDate,
        endDate
      );
      const { depositFinancialReport: deposits } = response;
      dispatch({
        type: ActionType.SetDeposits,
        payload: { deposits },
      });
    },
    async getOutstandingReports(orgId, startDate, endDate) {
      const { data: response } = await makeRequest<{
        outstandingFinancialReport: OutstandingReportRow[];
      }>(
        getOutstandingReport,
        LoadingType.GetOutstandingReports,
        orgId,
        startDate,
        endDate
      );
      const { outstandingFinancialReport: outstandingReports } = response;
      dispatch({
        type: ActionType.SetOutstandingReports,
        payload: { outstandingReports },
      });
    },
    async getTransactions(orgId, dateColumn, startDate, endDate) {
      const { data: response } = await makeRequest<{
        transactionFinancialReport: TransactionReport[];
      }>(
        getTransactions,
        LoadingType.GetTransactions,
        orgId,
        dateColumn,
        startDate,
        endDate
      );
      const { transactionFinancialReport: transactions } = response;
      dispatch({
        type: ActionType.SetTransactions,
        payload: { transactions },
      });
    },
    updateDepositsFilter: (key, value) => {
      dispatch({
        type: ActionType.UpdateFilter,
        payload: { key, value, reportType: ReportType.Deposits },
      });
    },
    updateOutstandingFilter: (key, value) => {
      dispatch({
        type: ActionType.UpdateFilter,
        payload: { key, value, reportType: ReportType.Outstanding },
      });
    },
    setOutstandingFilters: (filters) => {
      dispatch({
        type: ActionType.SetOutstandingFilters,
        payload: { filters },
      });
    },
    updateTransactionsFilter: (key, value) => {
      dispatch({
        type: ActionType.UpdateFilter,
        payload: { key, value, reportType: ReportType.Transactions },
      });
    },
    setTransactionsFilters(filters) {
      dispatch({
        type: ActionType.SetTransactionsFilters,
        payload: { filters },
      });
    },
  };
};
